<template>
  <transition name="FADE" mode="out-in">
    <div v-if="isNewMatch" class="BUTTON-CONTROLS">
      <button
          class="CALL-BUTTON"
          :class="{ 'x-pulse': isFetching }"
          :disabled="isFetching"
          selenium="call-button"
          @click.prevent="callConsumer($options.SUB_STATUS_OPTIONS.selected_show_number.text)">
        <span v-if="!isFetching" class="d-flex ai-center" selenium="job-details-show-number">
          <vk-icon name="phone" class="flex-0-0 h6 m-r-1" />
          {{ $options.JOBS_STATUS_ALIASES.new.ctas.call.label }}
        </span>
      </button>
      <router-link
          class="MESSAGE-BUTTON"
          replace
          :class="{ 'x-pulse': isFetching }"
          :disabled="isFetching"
          selenium="message-button"
          :to="messagesLink"
      >
        <span v-if="!isFetching" class="d-flex ai-center" selenium="job-details-show-message">
          <vk-icon name="chat-bubbles" class="flex-0-0 h6 m-r-1" />
          {{ $options.JOBS_STATUS_ALIASES.new.ctas.message.label }}
        </span>
      </router-link>
      <button
          v-if="$device.isDesktop || $device.isWide "
          class="PASS-BUTTON"
          :class="{ 'x-pulse': isFetching }"
          selenium="job-details-pass-button"
          :disabled="isFetching"
          @click.prevent="onPassOpen">
        <span v-if="!isFetching">{{ passButtonLabel }}</span>
      </button>
      <sub-status-modal
          :is-open="subStatusModalOpened"
          :phone="job.location.phone | phoneNumber"
          @primary="callConsumer"
          @secondary="onSubStatusClose"
          @update="update"/>
      <pass-modal
          :is-open="isPassModalOpened"
          @secondary="onPassClose"
          @primary="(...args) => onAction('pass', ...args)"/>
    </div>
    <div v-else-if="!isPastStatusAlias" class="ANCHOR-CONTROLS">
      <component
          :is="$device.isMobileOrTablet ? 'a' : 'span'"
          :href="$device.isMobileOrTablet ? `tel://${job.location.phone}` : ''"
          class="CALL-LINK"
          selenium="job-details-phone">
        <vk-icon v-if="job.location.phone" name="phone" class="flex-0-0 h5 m-r-1" />
        {{ job.location.phone | phoneNumber }}
        <span
            v-if="!job.location.phone && isFetching"
            class="x-pulse PLACEHOLDER" />
      </component>
      <a
          class="EMAIL-LINK wrap"
          :href="`mailto:${job.location.email}`"
          selenium="job-details-show-email-link">
        <vk-icon v-if="job.location.email" name="envelope-filled" class="flex-0-0 h5 m-r-1" />
        {{ job.location.email }}
        <span
            v-if="!job.location.email && isFetching"
            class="x-pulse PLACEHOLDER" />
      </a>
      <router-link
          class="MESSAGE-LINK"
          selenium="job-details-message"
          replace
          :to="messagesLink">
        <template v-if="job.location.phone">
          <vk-icon name="chat-bubbles" class="flex-0-0 h5 m-r-1" />
          {{ $options.JOBS_STATUS_ALIASES.new.ctas.message.label }}
        </template>
        <span v-if="!job.location.phone && isFetching" class="x-pulse PLACEHOLDER" />
      </router-link>
      <div v-if="!isFetching && $device.isMobileOrTablet && isPotentialMatch">
        <button
            class="UPDATE-BUTTON"
            :class="{ 'x-pulse': isFetching }"
            :disabled="isFetching"
            @click.prevent="onSubStatusModalOpen">
          <span class="d-flex ai-center">
            <img
                class="flex-0-0 h6 m-r-1"
                src="./icons/contact-book.svg"
                width="16"
                height="21"
            >
            Update Contact Status
          </span>
        </button>
        <div v-if="hasError && $device.isMobileOrTablet" class="c-red m-b-1">
          <vk-icon name="exclamation" class="ERROR-ICON m-r-1"/>
          Contact Status Update Failed, please try again.
        </div>
      </div>
      <sub-status-modal
          :is-open="subStatusModalOpened"
          :phone="job.location.phone | phoneNumber"
          @primary="subStatusUpdate"
          @secondary="onSubStatusClose"
          @update="update"/>
    </div>
  </transition>
</template>

<script>
import jobParams from '@/mixins/job-params';
import job from '@/mixins/job';
import passEducation from '@/views/Dashboard/mixins/pass-education';
import http from '@homeservices/api-kit/axios';
import { SUB_STATUS_OPTIONS } from '@/constants/sub-status-options';
import { addDays, isAfter } from 'date-fns';
import { mapActions } from 'vuex';
import SubStatusModal from './SecondaryCta/SubStatusModal';
import PassModal from './SecondaryCta/PassModal';

export default {
  SUB_STATUS_OPTIONS,
  name: 'primary-cta',
  components: {
    SubStatusModal,
    PassModal,
  },
  mixins: [
    passEducation,
    jobParams,
    job,
  ],
  data () {
    return {
      isPassModalOpened: false,
      subStatusModalOpened: false,
      hasError: false,
    };
  },
  computed: {
    isFetching () {
      return (
        this.currentJobMeta.status === this.$options.REQUEST_LIFECYCLE.FETCHING
      );
    },
    messagesLink () {
      return {
        name: 'dashboard::pro::jobs',
        params: this.$route.params,
        query: {
          ...this.$route.query,
          feature: 'messages',
        },
      };
    },
    isOverdue () {
      return isAfter(new Date(Date.now()), addDays(new Date(this.timeToExpire), 2))
        && this.$options.JOBS_STATUS_ALIASES.new.statuses.includes(this.match.status);
    },
    passButtonLabel () {
      return !this.isOverdue ? 'Pass & Refund' : this.$options.JOBS_STATUS_ALIASES.new.ctas.pass.label;
    },
  },
  methods: {
    ...mapActions('account/pro', ['updatePendo']),

    onSubStatusModalOpen () {
      this.subStatusModalOpened = true;
    },
    onSubStatusClose () {
      this.subStatusModalOpened = false;
    },
    onPassOpen () {
      this.isPassModalOpened = true;
    },
    async callConsumer (hasSubStatus) {
      this.subStatusUpdate(hasSubStatus, true);
      await this.transitionJob({
        action: this.$options.MATCH_STATUS_ACTIONS.CALL_CONSUMER,
        from: this.$options.JOBS_STATUS_ALIASES.new.key,
        to: this.$options.JOBS_STATUS_ALIASES.potential.key,
        order_hash: this.job.order_hash,
      });

      this.$router.replace({
        name: 'dashboard::pro::jobs',
        params: {
          jobsStatusAlias: this.$options.JOBS_STATUS_ALIASES.potential.key,
          jobId: this.job.order_hash,
        },
        query: this.$route.query,
      });

      this.$fireToast({
        message: this.$options.getMatchStatusActionsMessage({
          action: this.$options.MATCH_STATUS_ACTIONS.CALL_CONSUMER,
          firstName: this.job.location.first_name,
        }),
      });

      this.$events.$emit(this.$events.PRO_CALL, {
        jobID: this.job.order_hash,
      });

      this.$events.$emit(this.$events.PRO_CHANGE_STATUS, {
        jobID: this.job.id,
        changeTo: this.$options.JOBS_STATUS_ALIASES.potential.key,
      });

      await this.resetBusyPassCount();
      this.updatePendo('update');
    },
    async subStatusUpdate (selectedSubStatus, isInitial) {
      const eventToEmit = isInitial
        ? this.$events.SUB_STATUS_INITIAL
        : this.$events.SUB_STATUS_UPDATE;
      this.hasError = false;
      try {
        await http.put(`/platform/workorder/r/workorder/service-provider-sub-status/v1?order_hash=${this.job.order_hash}&service_provider_id=${this.userId}&service_provider_status=potential&service_provider_sub_status=${selectedSubStatus}`);
        this.$events.$emit(eventToEmit, { substatus: selectedSubStatus });
        this.update();
      } catch (err) {
        this.hasError = true;
      } finally {
        this.onSubStatusClose();
      }
    },
    update () {
      this.$emit('update');
    },
    onPassClose () {
      this.isPassModalOpened = false;
    },
    async onAction (ctaKey, params) {
      this.isPassModalOpened = false;
      const {
        action,
        from,
        to,
      } = this.$options.JOBS_STATUS_ALIASES.new.ctas[ctaKey];

      this.currentCtaKey = '';
      this.currentParams = undefined;
      this.isRequestingReview = false;

      await this.onStatusUpdate({
        action,
        from,
        to,
        params,
        order_hash: this.job.order_hash,
      });

      if (action === this.$options.MATCH_STATUS_ACTIONS.REJECT) {
        this.incrementCounters({ reason: params.reason });
      }
    },
    async onStatusUpdate (payload) {
      await this.transitionJob(payload);

      const isPastMatchStatus = payload.to === this.$options.JOBS_STATUS_ALIASES.past.key;

      this.$fireToast({
        message: this.$options.getMatchStatusActionsMessage({
          action: payload.action,
          firstName: this.job.location.first_name,
        }),
      });

      // If the user changed status towards active status –
      // we are changing jobsStatusAlias to the next status and we are also respecting jobId.
      // If the user changed status towards past status –
      // we don't want to navigate there unless it has unsaved changes.
      // Instead, we are navigating to the same jobsStatusAlias without jobId,
      // so fallback which is implemented for JobsList will automatically select the next job
      // (e.g. /dashboard/new redirects to /dashboard/new/<first_id_in_a_list>
      this.$router.replace({
        name: 'dashboard::pro::jobs',
        params: {
          jobsStatusAlias: isPastMatchStatus ? payload.from : payload.to,
          jobId: isPastMatchStatus ? undefined : payload.order_hash,
        },
        query: this.$route.query,
        // When job url transition is aborted but job status has already changed to past status -
        // we need to navigate there, so user won't lose its progress. See replace func signature
        // in VueRouter docs.
      }, () => null, () => this.$router.replace({
        name: 'dashboard::pro::jobs',
        params: {
          jobsStatusAlias: payload.to,
          jobId: payload.order_hash,
        },
        query: this.$route.query,
      }));
      if (payload.action === this.$options.MATCH_STATUS_ACTIONS.REJECT) {
        this.$events.$emit(this.$events.PRO_JOB_REJECT, {
          jobID: payload.jobId,
          reason: payload.reason,
          other: payload.explanation,
        });
      }
      this.updatePendo('update');
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";

  .BUTTON-CONTROLS {
    display: flex;
    flex: 0 1 auto;
  }

  .ANCHOR-CONTROLS {
    @include responsive(flex, 1 1 auto, $md: 0 1 auto);
    display: flex;
    flex-direction: column;
  }

  .CALL-BUTTON,
  .MESSAGE-BUTTON,
  .PASS-BUTTON {
    @include button-primary;
    @include button-small;
    @include responsive(flex, 1 1 50%, $md: 0 1 auto);
    @include responsive(width, auto, $md: 18*$unit);
    @include margin(1 0 1 2, $md: 0);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;

    &[disabled] {
      pointer-events: none;
    }
  }
  .UPDATE-BUTTON {
    @include button-secondary;
    @include button-small;
    @include responsive(flex, 1 1 100%, $md: 0 1 auto);
    @include responsive(width, auto, $md: 18*$unit);
    @include margin(1 0 1 2, $md: 0);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;

    &[disabled] {
      pointer-events: none;
    }
    @include margin(1 2 1 0, $md: 0 2 0 0);
  }
  .CALL-BUTTON,
  .MESSAGE-BUTTON,
  .PASS-BUTTON {
    @include margin(1 2 1 0, $md: 0 2 0 0);
    @include responsive(width, auto, $md: 18*$unit+4);
  }

  .CALL-LINK,
  .EMAIL-LINK,
  .MESSAGE-LINK {
    @include link-primary;
    @include margin(0 -2, $md: 0);
    @include padding(1 2, $md: 1 0);
    @include responsive(border-bottom, 1px solid $color-gray30, $md: none);
    display: flex;
    align-items: center;
    word-break: break-all;

    &:last-child {
      @include padding(1 2, $md: 1 0, $lg: 1 0 0);
      border-bottom: none;
    }
  }

  .CALL-LINK {
    @include desktop {
      @include link-black;
      @include hover(text-decoration, none, none);
      cursor: auto;
    }
  }

  .PLACEHOLDER {
    @include rect(100%, 16px);
    margin: $unit/2 0;

    @include desktop {
      @include rect(136px, 16px);
    }
  }
  .ERROR-ICON {
  top: 4px;
  position: relative;
  line-height: 18px;
  padding: 4px;
  font-size: 12px;
  background-color: $color-red;
  color: white;
}
</style>
